/**
 * Shared intent-led copy when shop/checkout requires a kiosk context but none
 * is present — matches ShopScreen tone (no monospace query dumps as hero text).
 *
 * EXC-CUST-P8-INTENTLED-DUAL-PRIMARY: auth + account stay dual `intent="primary"`
 * (Spec one-primary carve-out). Shop is an optional third action (`secondary`).
 */

import { useTranslation } from 'react-i18next';
import { Card } from '../../../shared/ui/Card.js';
import { Button } from '../../../shared/ui/Button.js';

export interface IntentLedKioskMissingPanelProps {
  /**
   * Shop URL had a malformed `kioskId` query. Ignored for the hero paragraph when
   * **`primaryText`** is set — explicit override wins.
   */
  invalidQuery: boolean;
  /** U1: guest chrome when no platform session after `/me`. */
  signedIn: boolean;
  onContinueToAuth: () => void;
  onGoToAccount: () => void;
  onSecondaryShop: () => void;
  /**
   * When set (e.g. checkout without session id), replaces the default shop hero
   * paragraph so one Card carries the full message. **`invalidQuery` is ignored** for
   * this paragraph — do not combine both unless callers accept that precedence.
   */
  primaryText?: string;
  /** Optional muted line below the primary paragraph. */
  helperText?: string;
}

export function IntentLedKioskMissingPanel({
  invalidQuery,
  signedIn,
  onContinueToAuth,
  onGoToAccount,
  onSecondaryShop,
  primaryText,
  helperText}: IntentLedKioskMissingPanelProps): JSX.Element {
  const { t } = useTranslation('customer');
  const bodyText = ((): string => {
    if (primaryText !== undefined) {
      return primaryText;
    }
    if (invalidQuery) {
      return t('shop.invalidLink');
    }
    return t('shop.missingKiosk');
  })();

  return (
    <>
      <Card padded>
        <p className="text-sm text-[var(--color-on-surface)]">{bodyText}</p>
        {helperText !== undefined ? (
          <p className="mt-2 text-xs text-[var(--color-on-surface-muted)]">{helperText}</p>
        ) : null}
      </Card>
      <div className="flex flex-col gap-2">
        {/* Guest: dual primary auth + account. Signed-in: never pair Sign In with shell avatar. */}
        {!signedIn ? (
          <Button intent="primary" size="lg" block onClick={onContinueToAuth}>
            {t('shop.continueToSignIn')}
          </Button>
        ) : null}
        <Button intent="primary" size="lg" block onClick={onGoToAccount}>
          {t('shop.goToAccount')}
        </Button>
        <Button intent="secondary" size="md" block onClick={onSecondaryShop}>
          {!signedIn ? t('shop.goToShopGuest') : t('shop.goToShop')}
        </Button>
      </div>
    </>
  );
}
